import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {sanitize} from '@github-ui/sanitize'
import {ChevronDownIcon, CopilotIcon} from '@primer/octicons-react'
import {Suspense, useMemo} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {IssuePlanBodyQuery as IssuePlanBodyQueryType} from './__generated__/IssuePlanBodyQuery.graphql'
import styles from './IssuePlanBody.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssuePlanBodyQuery = graphql`
  query IssuePlanBodyQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        planBodyHTML
      }
    }
  }
`

function IssuePlanBodyContent({issueId}: {issueId: string}) {
  const data = useLazyLoadQuery<IssuePlanBodyQueryType>(IssuePlanBodyQuery, {id: issueId})
  const planBodyHTML = data.node?.planBodyHTML

  if (!planBodyHTML) {
    return null
  }

  return <IssuePlanBodyView planBodyHTML={planBodyHTML} />
}

function IssuePlanBodyView({planBodyHTML}: {planBodyHTML: string}) {
  const sanitizedHTML = useMemo(() => sanitize(planBodyHTML), [planBodyHTML])

  return (
    <details className={styles.IssuePlanBody} open data-testid="issue-plan-body">
      <summary className={styles.Summary}>
        <span className={styles.Chevron}>
          <ChevronDownIcon size={16} />
        </span>
        <span className={styles.Icon}>
          <CopilotIcon size={16} />
        </span>
        <span className={styles.Title}>Implementation plan</span>
        <span className={styles.VersionBadge}>v1</span>
      </summary>
      <div className={styles.Content}>
        <MarkdownViewer verifiedHTML={sanitizedHTML} />
      </div>
    </details>
  )
}

export function IssuePlanBody({issueId}: {issueId: string}) {
  return (
    <ErrorBoundary boundaryName="IssuePlanBody" fallback={null} critical={false}>
      <Suspense fallback={null}>
        <IssuePlanBodyContent issueId={issueId} />
      </Suspense>
    </ErrorBoundary>
  )
}
